import { useEffect, useState } from 'react';

/**
 * Theme preference: three states, stored per-device in localStorage.
 *
 * 'system' is the default and is stored by REMOVING the key — so an absent
 * key means "follow the OS", which is what the pre-paint stamp in index.html
 * assumes too. Only an explicit light/dark choice is written.
 *
 * Per-device on purpose: the theme belongs to the screen you're looking at,
 * not the account (the same person on a bright laptop and a dark phone wants
 * different answers), and localStorage is readable before first paint, which
 * a server-held setting would not be — that's what avoids the flash.
 *
 * The resolved value is always stamped on <html> as data-theme="light|dark";
 * the CSS reads only that, never prefers-color-scheme, so resolution lives in
 * exactly one place.
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const KEY = 'theme';
const darkQuery = (): MediaQueryList => window.matchMedia('(prefers-color-scheme: dark)');

export function storedPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system'; // private mode: no memory, but the OS still decides
  }
}

export function resolveTheme(pref: ThemePref): Theme {
  if (pref === 'light' || pref === 'dark') return pref;
  return darkQuery().matches ? 'dark' : 'light';
}

export function applyPref(pref: ThemePref): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(pref));
  try {
    if (pref === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch {
    /* private mode; the choice just won't survive the tab */
  }
}

/** The header control's state. Re-stamps on change, and while 'system' is
 *  selected it follows the OS flipping mid-session. */
export function useThemePref(): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(storedPref);

  useEffect(() => {
    applyPref(pref);
    if (pref !== 'system') return;
    const mq = darkQuery();
    const onChange = () => applyPref('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  return [pref, setPref];
}
